import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
    getPushGroups, createPushGroup, updatePushGroup, deletePushGroup,
    addDomainsToGroup, removeDomainsFromGroup, getPushDomains,
} from '@/services/api/push';
import { PushNotificationGroup, PushDomain } from '@/types/push';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
    Loader2, Plus, Pencil, Trash2, Globe, Users, ChevronDown, ChevronUp, X, Search
} from 'lucide-react';

export default function Groups() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingGroup, setEditingGroup] = useState<PushNotificationGroup | null>(null);
    const [expandedGroup, setExpandedGroup] = useState<number | null>(null);
    const [showAddDomainDialog, setShowAddDomainDialog] = useState<number | null>(null);
    const [domainSearch, setDomainSearch] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    // Form state
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');

    const { data: groups = [], isLoading } = useQuery({
        queryKey: ['push-groups'],
        queryFn: getPushGroups,
    });

    const { data: allDomains = [] } = useQuery({
        queryKey: ['push-domains'],
        queryFn: getPushDomains,
    });

    // Only capture domains
    const captureDomains = allDomains.filter(d => d.purpose === 'capture');

    const refetchGroups = () => queryClient.refetchQueries({ queryKey: ['push-groups'] });

    const createMutation = useMutation({
        mutationFn: createPushGroup,
        onSuccess: async () => {
            toast({ title: 'Grupo criado!' });
            await refetchGroups();
            setShowCreateDialog(false);
            resetForm();
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updatePushGroup(id, data),
        onSuccess: async () => {
            toast({ title: 'Grupo atualizado!' });
            await refetchGroups();
            setEditingGroup(null);
            resetForm();
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deletePushGroup,
        onSuccess: async () => {
            toast({ title: 'Grupo deletado!' });
            await refetchGroups();
            setDeleteConfirm(null);
            setExpandedGroup(null);
        },
    });

    const addDomainMutation = useMutation({
        mutationFn: ({ groupId, domainIds }: { groupId: number; domainIds: number[] }) =>
            addDomainsToGroup(groupId, domainIds),
        onSuccess: async (updatedGroup) => {
            toast({ title: 'Dominio adicionado ao grupo!' });
            // Atualiza cache diretamente com a resposta da API
            queryClient.setQueryData<PushNotificationGroup[]>(['push-groups'], (old) => {
                if (!old) return old;
                return old.map(g => g.id === updatedGroup.id ? updatedGroup : g);
            });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message });
        },
    });

    const removeDomainMutation = useMutation({
        mutationFn: ({ groupId, domainIds }: { groupId: number; domainIds: number[] }) =>
            removeDomainsFromGroup(groupId, domainIds),
        onSuccess: async (updatedGroup) => {
            toast({ title: 'Dominio removido do grupo!' });
            // Atualiza cache diretamente com a resposta da API
            queryClient.setQueryData<PushNotificationGroup[]>(['push-groups'], (old) => {
                if (!old) return old;
                return old.map(g => g.id === updatedGroup.id ? updatedGroup : g);
            });
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro', description: error.message });
        },
    });

    const resetForm = () => {
        setFormName('');
        setFormDescription('');
    };

    const handleCreate = () => {
        if (!formName.trim()) return;
        createMutation.mutate({ name: formName.trim(), description: formDescription.trim() || undefined });
    };

    const handleUpdate = () => {
        if (!editingGroup || !formName.trim()) return;
        updateMutation.mutate({ id: editingGroup.id, data: { name: formName.trim(), description: formDescription.trim() || undefined } });
    };

    const openEditDialog = (group: PushNotificationGroup) => {
        setFormName(group.name);
        setFormDescription(group.description || '');
        setEditingGroup(group);
    };

    const getAvailableDomains = (group: PushNotificationGroup): PushDomain[] => {
        const existingIds = new Set((group.domains || []).map(d => d.id));
        let available = captureDomains.filter(d => !existingIds.has(d.id));
        if (domainSearch.trim()) {
            const q = domainSearch.toLowerCase();
            available = available.filter(d => d.domain.toLowerCase().includes(q));
        }
        return available;
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-start gap-5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg shadow-violet-200">
                        <Users className="h-7 w-7 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Grupos de Notificacao</h1>
                        <p className="mt-1 text-sm text-gray-500">Agrupe dominios de captura para enviar campanhas e flows para varios dominios ao mesmo tempo</p>
                    </div>
                </div>
                <Button
                    onClick={() => { resetForm(); setShowCreateDialog(true); }}
                    className="gap-2 bg-violet-600 hover:bg-violet-700 shadow-sm"
                >
                    <Plus className="h-4 w-4" /> Novo Grupo
                </Button>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white p-16">
                    <Loader2 className="h-7 w-7 animate-spin text-violet-500" />
                    <p className="mt-4 text-sm text-gray-500">Carregando grupos...</p>
                </div>
            )}

            {/* Groups List */}
            {!isLoading && groups.length > 0 && (
                <div className="space-y-4">
                    {groups.map((group) => {
                        const isExpanded = expandedGroup === group.id;
                        return (
                            <div
                                key={group.id}
                                className={`overflow-hidden rounded-2xl border transition-all duration-200 ${
                                    isExpanded
                                        ? 'border-violet-200 shadow-lg shadow-violet-50'
                                        : 'border-gray-100 bg-white shadow-sm hover:shadow-md hover:border-violet-100'
                                }`}
                            >
                                {/* Group Header */}
                                <div className="flex items-center justify-between p-5">
                                    <div
                                        className="flex items-center gap-4 flex-1 cursor-pointer"
                                        onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                                    >
                                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-50 to-violet-100">
                                            <Users className="h-6 w-6 text-violet-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{group.name}</h3>
                                            <div className="mt-1 flex items-center gap-3">
                                                {group.description && (
                                                    <span className="text-xs text-gray-500">{group.description}</span>
                                                )}
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">
                                                    <Globe className="h-3 w-3" />
                                                    {group.domain_count || 0} dominio{(group.domain_count || 0) !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => openEditDialog(group)} className="gap-1.5 text-xs">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(group.id)} className="gap-1.5 text-xs text-red-600 hover:text-red-700 hover:border-red-200">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setExpandedGroup(isExpanded ? null : group.id)} className="gap-1.5 text-xs">
                                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                </div>

                                {/* Expanded: Domains */}
                                {isExpanded && (
                                    <div className="border-t border-violet-100 bg-gradient-to-b from-gray-50/80 to-white p-5">
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="text-sm font-semibold text-gray-900">Dominios vinculados</h4>
                                            <Button size="sm" onClick={() => { setDomainSearch(''); setShowAddDomainDialog(group.id); }} className="gap-1.5 text-xs bg-violet-600 hover:bg-violet-700">
                                                <Plus className="h-3.5 w-3.5" /> Adicionar Dominio
                                            </Button>
                                        </div>

                                        {(group.domains && group.domains.length > 0) ? (
                                            <div className="space-y-2">
                                                {group.domains.map((domain) => (
                                                    <div key={domain.id} className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-3">
                                                        <div className="flex items-center gap-3">
                                                            <Globe className="h-4 w-4 text-gray-400" />
                                                            <span className="text-sm font-medium text-gray-900">{domain.domain}</span>
                                                            {domain.push_config?.mongo_domain_id ? (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Push ativo</span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-500">Nao configurado</span>
                                                            )}
                                                        </div>
                                                        <Button variant="ghost" size="sm" onClick={() => removeDomainMutation.mutate({ groupId: group.id, domainIds: [domain.id] })} disabled={removeDomainMutation.isPending} className="h-8 w-8 p-0 text-gray-400 hover:text-red-600">
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 p-8">
                                                <Globe className="h-6 w-6 text-gray-300" />
                                                <p className="mt-2 text-xs text-gray-500">Nenhum dominio vinculado</p>
                                                <p className="text-xs text-gray-400">Adicione dominios de captura a este grupo</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Empty State */}
            {!isLoading && groups.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-16">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50">
                        <Users className="h-8 w-8 text-gray-300" />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-gray-900">Nenhum grupo criado</h3>
                    <p className="mt-1.5 text-sm text-gray-500 text-center max-w-sm">
                        Crie um grupo de notificacao para agrupar dominios de captura e enviar campanhas/flows para todos de uma vez.
                    </p>
                    <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="mt-5 gap-2 bg-violet-600 hover:bg-violet-700">
                        <Plus className="h-4 w-4" /> Criar Primeiro Grupo
                    </Button>
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Novo Grupo de Notificacao</DialogTitle>
                        <DialogDescription>Crie um grupo para agrupar dominios de captura</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700">Nome</label>
                            <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Ex: Grupo Brasil, Grupo Internacional..." className="mt-1" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700">Descricao (opcional)</label>
                            <Input value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Descricao do grupo..." className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
                        <Button onClick={handleCreate} disabled={!formName.trim() || createMutation.isPending} className="bg-violet-600 hover:bg-violet-700">
                            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Criar Grupo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={!!editingGroup} onOpenChange={(open) => !open && setEditingGroup(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar Grupo</DialogTitle>
                        <DialogDescription>Altere as informacoes do grupo</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700">Nome</label>
                            <Input value={formName} onChange={(e) => setFormName(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700">Descricao</label>
                            <Input value={formDescription} onChange={(e) => setFormDescription(e.target.value)} className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditingGroup(null)}>Cancelar</Button>
                        <Button onClick={handleUpdate} disabled={!formName.trim() || updateMutation.isPending} className="bg-violet-600 hover:bg-violet-700">
                            {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Salvar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deletar Grupo</DialogTitle>
                        <DialogDescription>Tem certeza que deseja deletar este grupo? Os dominios nao serao afetados.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Deletar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Domain Dialog */}
            <Dialog open={!!showAddDomainDialog} onOpenChange={(open) => !open && setShowAddDomainDialog(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Adicionar Dominio ao Grupo</DialogTitle>
                        <DialogDescription>Selecione dominios de captura para adicionar</DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input placeholder="Pesquisar dominio..." value={domainSearch} onChange={(e) => setDomainSearch(e.target.value)} className="pl-9" />
                        </div>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                            {showAddDomainDialog && (() => {
                                const group = groups.find(g => g.id === showAddDomainDialog);
                                if (!group) return null;
                                const available = getAvailableDomains(group);
                                if (available.length === 0) {
                                    return (
                                        <div className="flex flex-col items-center py-8 text-gray-400">
                                            <Globe className="h-6 w-6" />
                                            <p className="mt-2 text-xs">
                                                {captureDomains.length === 0
                                                    ? 'Nenhum dominio de captura encontrado. Crie dominios com purpose "capture" primeiro.'
                                                    : 'Todos os dominios de captura ja estao neste grupo.'}
                                            </p>
                                        </div>
                                    );
                                }
                                return available.map((domain) => (
                                    <button
                                        key={domain.id}
                                        onClick={() => addDomainMutation.mutate({ groupId: showAddDomainDialog, domainIds: [domain.id] })}
                                        disabled={addDomainMutation.isPending}
                                        className="w-full flex items-center justify-between rounded-xl border border-gray-100 bg-white p-3 text-left hover:border-violet-200 hover:bg-violet-50/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Globe className="h-4 w-4 text-gray-400" />
                                            <span className="text-sm font-medium text-gray-900">{domain.domain}</span>
                                        </div>
                                        <Plus className="h-4 w-4 text-violet-500" />
                                    </button>
                                ));
                            })()}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
